(() => {
  const SELECTOR = '[data-cv-sbs]';

  function init(root) {
    if (!root || root.dataset.cvSbsReady === 'true') return;

    const tabs = Array.from(root.querySelectorAll('[data-cv-sbs-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-cv-sbs-panel]'));
    const filterButtons = Array.from(root.querySelectorAll('[data-cv-sbs-filter]'));
    if (!tabs.length || !panels.length) return;

    root.dataset.cvSbsReady = 'true';
    let activeFilter = 'all';

    const getFilterButton = (value) => filterButtons.find((button) => button.dataset.cvSbsFilterValue === value);
    const getFilterLabel = (value) => getFilterButton(value)?.dataset.cvSbsFilterLabel || 'All collections';
    const productLabel = (count) => (count === 1 ? 'product' : 'products');

    const getPanelForTab = (tab) => {
      const targetId = tab?.dataset.cvSbsTarget;
      return targetId ? root.querySelector(`#${CSS.escape(targetId)}`) : null;
    };

    const updatePanel = (panel, value) => {
      if (!panel) return 0;

      const cards = Array.from(panel.querySelectorAll('[data-cv-sbs-card]'));
      const result = panel.querySelector('[data-cv-sbs-filter-result]');
      const filterLabel = getFilterLabel(value);
      let matchCount = 0;

      cards.forEach((card) => {
        const collectionHandles = (card.dataset.cvSbsCollections || '').split('|').filter(Boolean);
        const matches = value === 'all' || collectionHandles.includes(value);
        card.hidden = !matches;
        if (matches) matchCount += 1;
      });

      if (result) {
        if (value === 'all') {
          result.hidden = true;
          result.textContent = '';
        } else if (matchCount) {
          result.hidden = false;
          result.textContent = `Showing ${matchCount} ${filterLabel} ${productLabel(matchCount)} in this scent family.`;
        } else {
          result.hidden = false;
          result.textContent = `No ${filterLabel} products are currently in this scent family.`;
        }
      }

      return matchCount;
    };

    const refreshTileCounts = (value) => {
      tabs.forEach((tab) => {
        const count = tab.querySelector('[data-cv-sbs-count]');
        if (!count) return;

        if (value === 'all') {
          const total = count.dataset.cvSbsTotal;
          if (total) count.textContent = `${total} products`;
          return;
        }

        const panel = getPanelForTab(tab);
        const matching = updatePanel(panel, value);
        count.textContent = `${matching} ${matching === 1 ? 'match' : 'matches'}`;
      });
    };

    const applyFilter = (value, options = {}) => {
      const requestedButton = getFilterButton(value);
      activeFilter = requestedButton ? value : 'all';

      filterButtons.forEach((button) => {
        const selected = button.dataset.cvSbsFilterValue === activeFilter;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      root.dataset.cvSbsActiveFilter = activeFilter;
      panels.forEach((panel) => updatePanel(panel, activeFilter));
      refreshTileCounts(activeFilter);

      if (options.updateUrl !== false && filterButtons.length) {
        const url = new URL(window.location.href);
        if (activeFilter === 'all') {
          url.searchParams.delete('collection');
        } else {
          url.searchParams.set('collection', activeFilter);
        }
        window.history.replaceState({}, '', url);
      }
    };

    const activate = (tab, options = {}) => {
      if (!tab) return;
      const targetPanel = getPanelForTab(tab);
      if (!targetPanel) return;

      tabs.forEach((item) => {
        const selected = item === tab;
        item.classList.toggle('is-active', selected);
        item.setAttribute('aria-selected', selected ? 'true' : 'false');
        item.setAttribute('tabindex', selected ? '0' : '-1');
      });

      panels.forEach((panel) => {
        panel.hidden = panel !== targetPanel;
      });

      applyFilter(activeFilter, { updateUrl: false });

      const slug = tab.dataset.cvSbsSlug;
      if (slug && options.updateUrl !== false) {
        const url = new URL(window.location.href);
        url.searchParams.set('scent', slug);
        window.history.replaceState({}, '', url);
      }

      if (options.focus) tab.focus();
      if (options.scroll) {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        targetPanel.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      }
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', (event) => {
        event.preventDefault();
        activate(tab, { scroll: true });
      });

      tab.addEventListener('keydown', (event) => {
        let nextIndex = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;

        if (nextIndex !== null) {
          event.preventDefault();
          activate(tabs[nextIndex], { focus: true, scroll: false });
        }
      });
    });

    filterButtons.forEach((button) => {
      button.addEventListener('click', () => {
        applyFilter(button.dataset.cvSbsFilterValue);
      });
    });

    const url = new URL(window.location.href);
    const requestedFilter = url.searchParams.get('collection');
    applyFilter(requestedFilter || 'all', { updateUrl: false });

    const requestedScent = url.searchParams.get('scent');
    const requestedTab = requestedScent
      ? tabs.find((tab) => tab.dataset.cvSbsSlug === requestedScent.toLowerCase())
      : null;

    if (requestedTab) {
      activate(requestedTab, { updateUrl: false, scroll: false });
    } else {
      tabs.forEach((tab, index) => {
        tab.classList.remove('is-active');
        tab.setAttribute('aria-selected', 'false');
        tab.setAttribute('tabindex', index === 0 ? '0' : '-1');
      });
      panels.forEach((panel) => {
        panel.hidden = true;
      });
    }
  }

  function initAll(scope = document) {
    scope.querySelectorAll(SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target.closest('[data-cv-sbs-block]');
    if (!block) return;
    const root = block.closest(SELECTOR);
    const tab = root && root.querySelector(`[data-cv-sbs-block="${CSS.escape(event.detail.blockId)}"]`);
    if (tab) tab.click();
  });
})();
